/**
 * Tech mapping (BUILD_PLAN.md sections 5.1 and 12.3): who each user is in
 * Autotask and in every service. A compact row per user, with a status chip
 * for each area, as Mappings → Clients has (the owner, 2026-10-19: "the techs
 * need the drop down like clients as their fields are getting crammed in").
 * Clicking anywhere on a user's row, or its arrow, opens them underneath
 * (TechDetails), which edits everything: their Autotask resource, SkySwitch
 * extension, ScreenConnect name and work computer, Datto RMM user, mailbox and
 * office addresses. One user is open at a time. The table never scrolls
 * sideways: narrower than a laptop, each row's chips wrap under the user, each
 * named.
 *
 * Each change saves at once, and the API checks it again.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";

import { ApiError } from "../../api/client";
import type { Tech, TechMapping } from "../../api/types";
import { useSession } from "../../app/session-context";
import { Panel } from "../../components/ui/Panel";
import { formatDateTimeIn } from "../../lib/dates";
import { TECH_MAPPING_KEY } from "./keys";
import { ChipCell, OpenArrow, OpenedRow } from "./SummaryRow";
import { type Status, summaryRowClass, useRowToggle } from "./summaryRows";
import { TechDetails } from "./TechPanel";
import {
  autotaskStatus,
  dattoStatus,
  mailStatus,
  officeStatus,
  screenConnectStatus,
  skySwitchStatus,
  userName,
} from "./techStatus";

/** Each area's column: its heading, and each tech's chip in it. */
const AREAS: readonly { name: string; status: (tech: Tech, mapping: TechMapping) => Status }[] = [
  { name: "Autotask", status: autotaskStatus },
  { name: "SkySwitch", status: skySwitchStatus },
  { name: "ScreenConnect", status: screenConnectStatus },
  { name: "Datto RMM", status: dattoStatus },
  { name: "Microsoft 365", status: mailStatus },
  { name: "Office addresses", status: officeStatus },
];

/** The table's columns, the user's and the arrow's included: the open tech spans them all. */
const COLUMNS = AREAS.length + 2;

function TechRow({
  tech,
  mapping,
  open,
  onOpen,
  onSaved,
}: {
  tech: Tech;
  mapping: TechMapping;
  open: boolean;
  onOpen: () => void;
  onSaved: (resourceId: number | null) => void;
}) {
  const id = useId();
  const nameId = `${id}-name`;
  const detailsId = `${id}-details`;

  return (
    <>
      <tr
        // Narrower than a laptop, a row is a card: the user and the arrow,
        // then the chips under them, each named, two or three to a line.
        className={summaryRowClass(open)}
        // The mouse's way in is the table body's click (useRowToggle);
        // the arrow is the keyboard's.
        data-tech-id={tech.id}
      >
        <td className="min-w-0 py-3 pr-3 pl-2 max-lg:basis-[calc(100%-3rem)]">
          <div id={nameId} className="font-medium">
            {userName(tech)}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted">
            {tech.email ? <span className="min-w-0 break-all">{tech.email}</span> : null}
            <span className="rounded-pill bg-brand-tint px-2 py-0.5 text-xs font-medium text-brand-ink-on-tint">
              {tech.role === "owner" ? "Owner" : "Tech"}
            </span>
            {!tech.is_active ? (
              <span className="rounded-pill bg-status-needs-client-bg px-2 py-0.5 text-xs font-medium text-status-needs-client-fg">
                Disabled
              </span>
            ) : null}
          </div>
        </td>
        {AREAS.map((area) => (
          <ChipCell
            key={area.name}
            name={area.name}
            status={area.status(tech, mapping)}
            // A chip on one line in a column; in a card's narrower one, it may wrap.
            className="max-sm:basis-1/2 sm:max-lg:basis-1/3 lg:[&>:last-child]:whitespace-nowrap"
          />
        ))}
        <td className="py-2 pr-2 text-right max-lg:w-12">
          <OpenArrow open={open} name={userName(tech)} controls={detailsId} onOpen={onOpen} />
        </td>
      </tr>
      {open ? (
        <OpenedRow columns={COLUMNS} rowClassName="max-lg:block" cellClassName="max-lg:block">
          <TechDetails
            id={detailsId}
            tech={tech}
            mapping={mapping}
            labelledBy={nameId}
            onSaved={onSaved}
          />
        </OpenedRow>
      ) : null}
    </>
  );
}

function TechTable({ mapping }: { mapping: TechMapping }) {
  const { me } = useSession();
  const queryClient = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const unmapped = mapping.techs.filter((t) => t.is_active && t.autotask_resource_id === null);
  const nobodys = mapping.screenconnect_names.filter((n) => n.mapped_user_id === null);

  // Anywhere on a tech's row opens or closes it; one is open at a time.
  const { bodyRef, onMouseDown, onClick } = useRowToggle("data-tech-id", (id) => {
    setOpenId((open) => (open === id ? null : id));
  });

  function saved(tech: Tech, resourceId: number | null) {
    queryClient.setQueryData<TechMapping>(TECH_MAPPING_KEY, (old) =>
      old
        ? {
            ...old,
            techs: old.techs.map((t) =>
              t.id === tech.id
                ? { ...t, autotask_resource_id: resourceId, suggested_resource_id: null }
                : t,
            ),
            resources: old.resources.map((r) => {
              if (r.autotask_resource_id === resourceId) {
                return { ...r, mapped_user_id: tech.id };
              }
              return r.mapped_user_id === tech.id ? { ...r, mapped_user_id: null } : r;
            }),
          }
        : old,
    );
    void queryClient.invalidateQueries({ queryKey: TECH_MAPPING_KEY });
  }

  return (
    <div className="flex flex-col gap-3">
      <p role="status" className="text-muted">
        {`${String(mapping.techs.length)} users · ${String(unmapped.length)} active without an Autotask resource`}
      </p>
      {/* Never sideways (BUILD_PLAN 11.3; #220): a chip per area fits 1024 px
          and wider, and narrower each row wraps its chips. */}
      <div>
        <table className="w-full text-left max-lg:block">
          <thead className="text-[12.5px] font-medium uppercase tracking-[0.08em] text-muted max-lg:hidden">
            <tr>
              <th scope="col" className="pb-2 pl-2 pr-3">
                User
              </th>
              {AREAS.map((area) => (
                <th key={area.name} scope="col" className="pb-2 pr-3">
                  {area.name}
                </th>
              ))}
              <th scope="col" className="w-12 pb-2">
                <span className="sr-only">Open</span>
              </th>
            </tr>
          </thead>
          <tbody ref={bodyRef} className="max-lg:block" onMouseDown={onMouseDown} onClick={onClick}>
            {mapping.techs.map((tech) => (
              <TechRow
                key={tech.id}
                tech={tech}
                mapping={mapping}
                open={tech.id === openId}
                onOpen={() => {
                  setOpenId((open) => (open === tech.id ? null : tech.id));
                }}
                onSaved={(resourceId) => {
                  saved(tech, resourceId);
                }}
              />
            ))}
          </tbody>
        </table>
      </div>
      {nobodys.length > 0 ? (
        <p className="text-sm text-muted">
          {`ScreenConnect names seen in the last 7 days that aren't anyone's: ${nobodys
            .map((n) => `${n.name} (${formatDateTimeIn(n.last_seen, me.timezone)})`)
            .join(", ")}. Their sessions aren't on anyone's day.`}
        </p>
      ) : null}
    </div>
  );
}

export function TechMappingSection() {
  const { api } = useSession();
  const mapping = useQuery({
    queryKey: TECH_MAPPING_KEY,
    queryFn: () => api.getTechMapping(),
    retry: (failures, error) =>
      !(error instanceof ApiError && error.status < 500) && failures < 2,
  });

  let body;
  if (mapping.error) {
    body = (
      <p role="alert" className="text-muted">
        The tech mapping could not be loaded. Reload the page to try again.
      </p>
    );
  } else if (!mapping.data) {
    body = (
      <p role="status" className="text-muted">
        Loading…
      </p>
    );
  } else {
    body = (
      <>
        {mapping.data.resources.length === 0 ? (
          <p className="mb-3 text-muted">
            No Autotask resources yet. They arrive with the Autotask sync, which runs every night;
            Owner tools → Autotask → Sync Autotask now runs it now.
          </p>
        ) : null}
        <TechTable mapping={mapping.data} />
      </>
    );
  }

  return (
    <Panel aria-labelledby="tech-mapping-heading">
      <h2 id="tech-mapping-heading" className="text-lg font-semibold tracking-tight">
        Techs
      </h2>
      <p className="mt-1 text-muted">
        Everyone who has signed in to Axon Time, and who they are in Autotask. Time is logged as
        that resource, with its default role. Someone appears here after their first sign-in.
        Their mailbox is the address they sign in with, so someone who hasn&apos;t signed in has
        no mailbox yet. Their mail is read only while its switch is on and they&apos;re in the
        techs&apos; group in Exchange. A work computer is a tech&apos;s own computer at the office:
        their ScreenConnect sessions into it don&apos;t count, and make no blocks. Its address
        on the office network is their office address, read from ScreenConnect every hour. Click
        a tech to see and change everything about them.
      </p>
      <div className="mt-4">{body}</div>
    </Panel>
  );
}
